import React, { forwardRef, RefObject } from 'react';
import { ButtonRoot, IButtonRoot } from '../../components/Button/ButtonRoot';
import { Icon, IIconProps } from '../../components/Icon/Icon';
import { Badge, BadgeSize, IBadgeProps } from '../../components/Badge/Badge';
import { P, Span, useTextCss } from '../../components/Texts/Texts';
import { IDropdownBaseProps } from '../Dropdown/DropdownContext';
import { StyleIdentifier, useDropdown } from '../Dropdown/useDropdown';
import { SIZES } from '../../fundamentals/sizes/sizes';
import styled, { css, useTheme } from 'styled-components';
import { FONT_SIZES, FONT_WEIGHTS, FontSize, FontWeight } from '../../fundamentals/typography/typography';
import { IAllowableGutterProps, IGapProps, IGutterProps, useGapCss, useGutterCss } from '../../fundamentals/spaces/space';
import { GapContainer } from '../../fundamentals/spaces/SpaceContainer';
import { IButtonProps } from '../../components/Button/Button';
import { INoTextDecorationProps, useFocusCss } from '../../fundamentals/actions';
import { HR } from '../../components/Divider/Divider';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { IMenuProps } from '../Menu/Menu';
import { IMenuItemPopperProps } from '../MenuItem/MenuItemPopper';
import { BaseDiv } from '../../common/BaseElements';
import { Logo, LogoSize } from '../../components/Logo/Logo';

type DropdownIconProps = Omit<IIconProps, 'size'>;

export interface IDropdownItemBaseProps
  extends React.PropsWithChildren<
    IDropdownBaseProps & Pick<IButtonProps, 'disabled' | 'key'> & Pick<IMenuProps, 'compact'> & Pick<IMenuItemPopperProps, 'popperConfig'>
  > {
  leadingIcon?: DropdownIconProps;
  leadingIconLogo?: boolean;
  postLeadingIcon?: DropdownIconProps;
  label?: string;
  badge?: Omit<IBadgeProps, 'size'>;
  suffixText?: string;
  trailingIcon?: DropdownIconProps;
  postTrailingIcon?: DropdownIconProps;
  divider?: boolean | 'top' | 'bottom';
  isTitle?: boolean;
  asContainer?: boolean;
  asMenu?: boolean;
  onClick?: React.MouseEventHandler;
  compact?: boolean;
  active?: boolean;
  selected?: boolean;
  menuPlacement?: 'top' | 'bottom';
}

export const dropDownIconSize: Record<StyleIdentifier, IIconProps['size']> = {
  default: SIZES.MICRO,
  mobile: SIZES.X_SMALL,
  compact: SIZES.XX_SMALL
};

const textSizes: Record<StyleIdentifier, FontSize> = {
  default: FONT_SIZES.TEXT_SMALL,
  mobile: FONT_SIZES.TEXT_DEFAULT,
  compact: FONT_SIZES.TEXT_NANO
};

const fontWeights: Record<StyleIdentifier, Record<'default' | 'title', FontWeight>> = {
  default: {
    default: FONT_WEIGHTS.REGULAR,
    title: FONT_WEIGHTS.SEMI_BOLD
  },
  mobile: {
    default: FONT_WEIGHTS.MEDIUM,
    title: FONT_WEIGHTS.EXTRA_BOLD
  },
  compact: {
    default: FONT_WEIGHTS.MEDIUM,
    title: FONT_WEIGHTS.EXTRA_BOLD
  }
};

const gaps: Record<StyleIdentifier, IAllowableGutterProps> = {
  default: 12,
  mobile: 16,
  compact: false
};

const gutters: Record<StyleIdentifier, IAllowableGutterProps> = {
  default: 10,
  mobile: 12,
  compact: 12
};

const sideGutters: Record<'default' | 'selected', Record<StyleIdentifier, IAllowableGutterProps>> = {
  default: {
    default: 16,
    mobile: 16,
    compact: 4
  },
  selected: {
    default: 12,
    mobile: 12,
    compact: false
  }
};

const iconGutter: Record<StyleIdentifier, IAllowableGutterProps> = {
  default: 2,
  mobile: false,
  compact: false
};

const badgeSize: Record<StyleIdentifier, BadgeSize> = {
  default: SIZES.XXX_SMALL,
  mobile: SIZES.XX_SMALL,
  compact: SIZES.XX_SMALL
};

const suffixTextSize: Record<StyleIdentifier, FontSize> = {
  default: FONT_SIZES.TEXT_MICRO,
  mobile: FONT_SIZES.TEXT_DEFAULT,
  compact: FONT_SIZES.TEXT_DEFAULT
};

export const getGutters = (styleIdentifier: StyleIdentifier, selected?: boolean): IGutterProps => ({
  gutterTop: gutters[styleIdentifier],
  gutterBottom: gutters[styleIdentifier],
  gutterLeft: sideGutters[selected ? 'selected' : 'default'][styleIdentifier],
  gutterRight: sideGutters.default[styleIdentifier]
});

const Suffix = styled(Span)`
  color: ${({ theme }) => theme.dropdownColours.suffix.text};
`;

type ItemBaseCss = { styleIdentifier: StyleIdentifier } & IGapProps & IGutterProps & Pick<IDropdownItemBaseProps, 'disabled'>;

export const MenuItemPopperContainer = styled(BaseDiv)<{ siblingHeight?: number }>`
  height: ${({ siblingHeight }) => `${siblingHeight}px`};
  margin-top: ${({ siblingHeight }) => `-${siblingHeight}px`};
`;

export const useDropdownItemBaseCss = css<ItemBaseCss>`
  width: 100%;
  text-align: left;
  align-items: flex-start;
  justify-content: left;
  ${useGapCss()};
  ${useGutterCss()};

  ${({ styleIdentifier }) =>
    styleIdentifier === 'compact' &&
    css`
      flex-direction: column;
      align-items: center;
    `}

  &:first-child,
  ${MenuItemPopperContainer} + &:first-of-type {
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;
  }

  &:last-child {
    border-bottom-left-radius: inherit;
    border-bottom-right-radius: inherit;
  }
`;

const useHoverOnClickCss = css<Pick<IDropdownItemBaseProps, 'onClick' | 'active' | 'selected' | 'asMenu'>>`
  ${({ theme, onClick }) =>
    onClick &&
    css`
      &:hover {
        background: ${theme.dropdownColours.item.hover.background};
      }

      &:active {
        background: ${theme.dropdownColours.item.active.background};
      }
    `}

  ${({ active, theme }) =>
    active &&
    css`
      background: ${theme.dropdownColours.item.active.background};
    `}

  ${({ selected, theme, asMenu }) =>
    selected &&
    asMenu &&
    css`
      border-left: ${theme.dropdownColours.item.active.border};
      background: ${theme.dropdownColours.item.active.background};
    `}
`;

// const BrandLogo: React.FC<{ styleIdentifier: StyleIdentifier }> = ({ styleIdentifier, ...rest }) => {
//   const useTheme();
//   return <Logo size={dropDownIconSize[styleIdentifier] as LogoSize} variant='black' placement='mark' />;
// };

const DropdownButton = styled(ButtonRoot)<{ styleIdentifier: StyleIdentifier } & IGapProps & IGutterProps & INoTextDecorationProps & IButtonRoot>`
  ${useDropdownItemBaseCss};
  color: ${({ theme }) => theme.colours.text.default};
  ${useTextCss};
  ${useFocusCss(true)};

  ${({ disabled, theme }) => {
    if (disabled) {
      return css`
        opacity: ${theme.dropdownColours.disabled.opacity};
      `;
    }

    return useHoverOnClickCss;
  }}
`;

const PostItemContainer = styled(GapContainer)<Pick<IDropdownItemBaseProps, 'compact'>>`
  margin-left: auto;
  flex-shrink: 0;

  ${({ compact }) =>
    compact &&
    css`
      position: absolute;
      top: 50%;
      right: 0;
      transform: translate(-50%, -50%);
    `}
`;

const DropdownItemChildren = ({
  children,
  leadingIcon,
  postLeadingIcon,
  badge,
  suffixText,
  trailingIcon,
  postTrailingIcon,
  label,
  isMobile,
  compact,
  isTablet,
  leadingIconLogo
}: IDropdownItemBaseProps) => {
  const { styleIdentifier } = useDropdown({ isTablet, isMobile });
  const identifier = compact ? 'compact' : styleIdentifier;
  const theme = useTheme();

  const extraIconProps = {
    gutterTop: iconGutter[identifier],
    size: dropDownIconSize[identifier]
  };

  const BrandLogo = () => <Logo size={dropDownIconSize[identifier] as LogoSize} variant={theme.isDarkMode ? 'white' : 'black'} placement='mark' />;

  return (
    <>
      {compact ? (
        <>
          {(leadingIcon || postLeadingIcon) && !leadingIconLogo && <Icon {...extraIconProps} {...(postLeadingIcon ?? leadingIcon)} gutterBottom={4} />}
          {leadingIconLogo && <BrandLogo />}
        </>
      ) : (
        <>
          {leadingIcon && !leadingIconLogo && <Icon {...extraIconProps} {...leadingIcon} />}
          {postLeadingIcon && !leadingIconLogo && <Icon {...extraIconProps} {...postLeadingIcon} />}
          {leadingIconLogo && <BrandLogo />}
        </>
      )}

      {label}
      {compact ? !label && children : children}
      {(badge || suffixText || trailingIcon || postTrailingIcon) && (
        <PostItemContainer compact={compact} gap={gaps[identifier]}>
          {compact ? (
            <>{(trailingIcon || postTrailingIcon) && <Icon {...extraIconProps} size={SIZES.X_MICRO} {...(postTrailingIcon ?? trailingIcon)} />}</>
          ) : (
            <>
              {badge && <Badge size={badgeSize[identifier]} gutterTop={2} {...badge} />}
              {suffixText && <Suffix textSize={suffixTextSize[identifier]}>{suffixText}</Suffix>}
              {trailingIcon && <Icon {...extraIconProps} {...trailingIcon} />}
              {postTrailingIcon && <Icon {...extraIconProps} {...postTrailingIcon} />}
            </>
          )}
        </PostItemContainer>
      )}
    </>
  );
};

const Title = styled(P)<ItemBaseCss>`
  ${useDropdownItemBaseCss};
`;

const CustomContainer = styled(BaseDiv)<ItemBaseCss>`
  ${useDropdownItemBaseCss};
`;

export const DropdownBaseItem = forwardRef<HTMLButtonElement | HTMLParagraphElement | HTMLDivElement, IDropdownItemBaseProps>(
  ({ divider, asContainer, isTitle, compact, label, ...rest }, ref) => {
    const { styleIdentifier, asMenu } = useDropdown(rest);
    const { generateTestId } = useTestId(rest, 'DropdownItem');

    const identifier: StyleIdentifier = compact ? 'compact' : styleIdentifier;

    const extraProps = {
      ...getGutters(identifier, rest.selected && rest.asMenu),
      textSize: textSizes[identifier],
      fontWeightCss: fontWeights[identifier][isTitle ? 'title' : 'default'],
      gap: gaps[identifier],
      styleIdentifier: identifier,
      asMenu,
      selected: rest.selected
    };

    const showTopDivider = divider === true || divider === 'top';
    const showBottomDivider = divider === true || divider === 'bottom';

    const handleOnClick: React.MouseEventHandler<HTMLButtonElement | HTMLParagraphElement | HTMLDivElement> = (e) => {
      // remove focus for non-keyboard
      if (e.detail) {
        e.currentTarget?.blur?.();
      }

      rest.onClick?.(e);
      e.stopPropagation();
    };

    let child = (
      <DropdownButton ref={ref as RefObject<HTMLButtonElement>} {...extraProps} {...rest} noTextDecoration {...generateTestId('btn')} noWrap>
        <DropdownItemChildren compact={compact} label={label} {...rest} />
      </DropdownButton>
    );

    if (isTitle && !rest.onClick) {
      child = (
        <Title noTextDecoration ref={ref as RefObject<HTMLParagraphElement>} {...extraProps} {...rest} onClick={handleOnClick} {...generateTestId('title')}>
          <DropdownItemChildren compact={compact} label={label} {...rest} />
        </Title>
      );
    } else if (asContainer) {
      child = (
        <CustomContainer {...extraProps} {...rest} onClick={handleOnClick} {...generateTestId('custom')}>
          <DropdownItemChildren compact={compact} label={label} {...rest} />
        </CustomContainer>
      );
    }

    return (
      <>
        {showTopDivider && <HR light />}
        {child}
        {showBottomDivider && <HR light />}
      </>
    );
  }
);
